import {useQuery} from '@apollo/client';
import {useEffect, useState, useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import {GET_REPORT_OPERATORS, GetReportOperatorsResponse} from '../graphql/queries/getReportOperators';
import {GET_POD_REVIEW_REVIEWERS, GetPodReviewReviewersResponse} from '../graphql/queries/getPodReviewReviewers';
import {getUserInfo, isReportDownloadSelfOnlyRole} from '../../../../utils/auth';
import {ReportFileFiltersType} from './useCentralReportFileQuery';
import {getLastWeekDateTimeRange} from '../../../../utils/dateTimeUtils';

interface UseReportOperatorsResult {
    operatorOptions: {value: string, label: string}[];
    loading: boolean;
    defaultFilters: ReportFileFiltersType | null;
}

interface UseReportOperatorsParams {
    isFromQualityControl?: boolean;
}

export const useReportOperators = (params?: UseReportOperatorsParams): UseReportOperatorsResult => {
    const { isFromQualityControl = false } = params || {};
    const {t, i18n} = useTranslation();
    const [operatorOptions, setOperatorOptions] = useState<{value: string, label: string}[]>([]);
    const [defaultFilters, setDefaultFilters] = useState<ReportFileFiltersType | null>(null);
    
    // 获取"全部"的翻译文本，确保翻译已加载
    const allLabel = useMemo(() => {
        const translated = t('central.download.filters.all');
        // 如果返回的是 key 本身，说明翻译未找到，使用默认值
        return translated === 'central.download.filters.all' ? (i18n.language === 'zh' ? '全部' : 'All') : translated;
    }, [t, i18n.language]);
    
    // 根据 isFromQualityControl 选择不同的查询
    const { data: operatorsData, loading: operatorsLoading } = useQuery<GetReportOperatorsResponse>(
        GET_REPORT_OPERATORS,
        { skip: isFromQualityControl }
    );
    
    const { data: reviewersData, loading: reviewersLoading } = useQuery<GetPodReviewReviewersResponse>(
        GET_POD_REVIEW_REVIEWERS,
        { skip: !isFromQualityControl }
    );

    const loading = operatorsLoading || reviewersLoading;

    // 处理操作人数据变化并设置默认过滤条件
    useEffect(() => {
        if (isFromQualityControl && reviewersData?.getPodReviewReviewers) {
            // 设置审核人选项
            const options = [
                { value: '', label: allLabel },
                ...reviewersData.getPodReviewReviewers.map(reviewer => ({
                    value: reviewer.id,
                    label: `${reviewer.name}`
                }))
            ];
            setOperatorOptions(options);

            // 设置默认过滤条件
            const currentUser = getUserInfo();
            // 设置默认日期为最近一周
            const [startDate, endDate] = getLastWeekDateTimeRange();
            let filters: ReportFileFiltersType = {
                type: undefined,
                name: undefined,
                reportDate: [startDate, endDate],
                operatorId: undefined,
            };

            if (currentUser) {
                const currentUserId = parseInt(currentUser.account.id);
                const currentReviewer = reviewersData.getPodReviewReviewers.find(
                    reviewer => parseInt(reviewer.id) === currentUserId
                );
                if (currentReviewer) {
                    filters.operatorId = currentUserId;
                }
            }

            setDefaultFilters(filters);
        } else if (!isFromQualityControl && operatorsData?.getReportOperators) {
            const selfOnly = isReportDownloadSelfOnlyRole();
            const mapped = operatorsData.getReportOperators.map(operator => ({
                value: operator.id,
                label: `${operator.name}`
            }));
            // 总部/仓库等角色后端仅本人：不提供「全部」，避免误以为可查他人
            const options = selfOnly ? mapped : [{ value: '', label: allLabel }, ...mapped];
            setOperatorOptions(options);

            // 设置默认过滤条件
            const currentUser = getUserInfo();
            // 设置默认日期为最近一周
            const [startDate, endDate] = getLastWeekDateTimeRange();
            let filters: ReportFileFiltersType = {
                type: undefined,
                name: undefined,
                reportDate: [startDate, endDate],
                operatorId: undefined,
            };

            if (currentUser) {
                const currentUserId = parseInt(currentUser.account.id, 10);
                if (selfOnly) {
                    filters.operatorId = currentUserId;
                } else {
                    const currentOperator = operatorsData.getReportOperators.find(
                        operator => parseInt(operator.id, 10) === currentUserId
                    );
                    if (currentOperator) {
                        filters.operatorId = currentUserId;
                    }
                }
            }

            setDefaultFilters(filters);
        }
    }, [operatorsData, reviewersData, isFromQualityControl, allLabel]);

    return {
        operatorOptions,
        loading,
        defaultFilters
    };
};
